import { useCallback } from 'react';
import { useRoutePlanStore } from '../store/routePlanStore';
import type { PointSourceMode, RouteEndpoint } from '../store/routePlanStore';
import type { MapEditorCoordinator } from '../controller/MapEditorCoordinator';
import type { RoutePlanFloatPanelProps } from '../components/RoutePlanFloatPanel';

/**
 * 路径规划浮层业务回调钩子
 *
 * 层级：hooks（"React 生命周期"）
 *
 * 职责：
 * 1. 订阅 routePlanStore，组装 RoutePlanFloatPanel 的全部 props
 * 2. 回调内通过 coordinatorRef.current?.routePlan 调用 Manager（依赖 ref 对象，规范 3 方案 B）
 * 3. 输入框 onChange 直接写 store（纯文本状态，无需经过 Manager）
 *
 * 返回的回调引用稳定，可直接传给 RoutePlanFloatPanel
 */
export function useRoutePlan(
  coordinatorRef: React.MutableRefObject<MapEditorCoordinator | null>
): RoutePlanFloatPanelProps {
  const pointSourceMode = useRoutePlanStore((s) => s.pointSourceMode);
  const routeStage = useRoutePlanStore((s) => s.routeStage);
  const startInput = useRoutePlanStore((s) => s.startInput);
  const endInput = useRoutePlanStore((s) => s.endInput);
  const startInputError = useRoutePlanStore((s) => s.startInputError);
  const endInputError = useRoutePlanStore((s) => s.endInputError);
  const hasStartPoint = useRoutePlanStore((s) => s.startPoint !== null);
  const hasEndPoint = useRoutePlanStore((s) => s.endPoint !== null);
  const distance = useRoutePlanStore((s) => s.distance);
  const duration = useRoutePlanStore((s) => s.duration);
  const planError = useRoutePlanStore((s) => s.planError);

  const onSwitchSourceMode = useCallback(
    (mode: PointSourceMode) => {
      coordinatorRef.current?.routePlan.switchSourceMode(mode);
    },
    [coordinatorRef]
  );

  const onStartInputChange = useCallback((text: string) => {
    useRoutePlanStore.getState().setStartInput(text);
  }, []);

  const onEndInputChange = useCallback((text: string) => {
    useRoutePlanStore.getState().setEndInput(text);
  }, []);

  const onApplyInputPoint = useCallback(
    (which: RouteEndpoint) => {
      coordinatorRef.current?.routePlan.applyInputPoint(which);
    },
    [coordinatorRef]
  );

  const onStartPlan = useCallback(() => {
    coordinatorRef.current?.routePlan.startPlan();
  }, [coordinatorRef]);

  const onResetPoints = useCallback(() => {
    coordinatorRef.current?.routePlan.resetPoints();
  }, [coordinatorRef]);

  const onClearRoute = useCallback(() => {
    coordinatorRef.current?.routePlan.clearRoute();
  }, [coordinatorRef]);

  return {
    pointSourceMode,
    routeStage,
    startInput,
    endInput,
    startInputError,
    endInputError,
    hasStartPoint,
    hasEndPoint,
    distance,
    duration,
    planError,
    onSwitchSourceMode,
    onStartInputChange,
    onEndInputChange,
    onApplyInputPoint,
    onStartPlan,
    onResetPoints,
    onClearRoute,
  };
}
